import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators'

import { Injectable } from '@angular/core'

import { AssetSearchResult, AssetService, FileSearchResult } from './asset.service'

export type SearchQuery = string
export type SearchResult = AssetSearchResult | FileSearchResult

@Injectable({
    providedIn: 'root'
})
export class SearchService {
    private _searchQuery$ = new Subject<SearchQuery>()
    private _loadingSource = new BehaviorSubject<boolean>(false)
    private _errorSource = new BehaviorSubject<string>(null)
    private _lastQuery: string = ''

    public loading$ = this._loadingSource.asObservable()
    public error$ = this._errorSource.asObservable()

    get lastQuery(): string {
        return this._lastQuery
    }
    public results$: Observable<SearchResult[]> = this._searchQuery$.pipe(
        debounceTime(300),
        map(query => query?.trim() ?? ''),
        distinctUntilChanged(),
        switchMap(query => {
            if (!query) {
                this._loadingSource.next(false)
                this._errorSource.next(null)
                return of([])
            }

            this._loadingSource.next(true)
            return combineLatest([
                this._assetService.searchAssetsByName(query).pipe(
                    catchError(err => {
                        console.error('[SearchService] Name search error:', err)
                        return of<AssetSearchResult[]>([])
                    })
                ),
                this._assetService.searchAssetsBySemantics(query).pipe(
                    catchError(err => {
                        console.error('[SearchService] Semantic search error:', err)
                        return of<FileSearchResult[]>([])
                    })
                )
            ]).pipe(
                map(([namedResults, fileResults]) => this.mergeResults(namedResults, fileResults)),
                tap(() => this._errorSource.next(null)),
                catchError(error => {
                    console.error('[SearchService] Outer error:', error)
                    this._errorSource.next('Unable to search assets right now.')
                    return of([])
                }),
                finalize(() => {
                    this._loadingSource.next(false)
                })
            )
        }),
        shareReplay(1)
    )

    constructor(private _assetService: AssetService) { }

    searchAssets(query: SearchQuery) {
        this._lastQuery = query
        this._searchQuery$.next(query)
    }

    reset() {
        this._errorSource.next(null)
        this._loadingSource.next(false)
        this._searchQuery$.next('')
    }

    private mergeResults(nameResults: AssetSearchResult[], fileResults: FileSearchResult[]): SearchResult[] {
        const results: SearchResult[] = []
        const seenAssets = new Set<number>()
        const seenFiles = new Set<number | string>()

        // Add name-based asset results first (they're exact matches)
        nameResults.forEach(result => {
            seenAssets.add(result.id)
            results.push(result)
        })

        // Add file results from semantic search (deduplicate by file ID or S3 ref ID)
        fileResults.forEach(result => {
            const dedupeKey = result.fileId != null ? result.fileId : `s3-${result.s3ObjectReferenceId}`
            if (!seenFiles.has(dedupeKey)) {
                seenFiles.add(dedupeKey)
                results.push(result)
            }
        })

        return results
    }
}
